import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { formatCurrency } from '../../utils/payrollCalculations';

interface PayrollComponent {
  id: number;
  name: string;
  type: 'allowance' | 'deduction';
  amount: number | null;
  percentage: number | null;
  calculationType: string;
  calculationBase: string;
  description: string | null;
  isActive: boolean;
  category: string | null;
}

const PayrollComponents: React.FC = () => {
  const [components, setComponents] = useState<PayrollComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'allowances' | 'deductions'>('allowances');
  const [showModal, setShowModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState<PayrollComponent | null>(null);
// Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'allowance' as 'allowance' | 'deduction',
    amount: '',
    percentage: '',
    calculationType: 'fixed',
    calculationBase: 'baseSalary',
    description: '',
    category: '',
    isActive: true,
  });

  useEffect(() => {
    fetchComponents();
  }, []);

  const fetchComponents = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(getApiUrl('payroll/components'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComponents(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching components');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token') || '';
      const payload = {
        name: formData.name,
        type: formData.type,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        percentage: formData.percentage ? parseFloat(formData.percentage) : null,
        calculationType: formData.calculationType,
        calculationBase: formData.calculationBase,
        description: formData.description || null,
        category: formData.category || null,
        isActive: formData.isActive,
      };

      if (editingComponent) {
        await axios.put(getApiUrl(`payroll/components/${editingComponent.id}`), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Component updated successfully');
      } else {
        await axios.post(getApiUrl('payroll/components'), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Component created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchComponents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving component');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (component: PayrollComponent) => {
    setEditingComponent(component);
    setFormData({
      name: component.name,
      type: component.type,
      amount: component.amount?.toString() || '',
      percentage: component.percentage?.toString() || '',
      calculationType: component.calculationType || 'fixed',
      calculationBase: component.calculationBase || 'baseSalary',
      description: component.description || '',
      category: component.category || '',
      isActive: component.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;

    try {
      const token = localStorage.getItem('token') || '';
      await axios.delete(getApiUrl(`payroll/components/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Component deleted successfully');
      fetchComponents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting component');
    }
  };

  const handleToggleActive = async (component: PayrollComponent) => {
    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/components/${component.id}`), 
        { isActive: !component.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchComponents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating component');
    }
  };

  const resetForm = () => {
    setEditingComponent(null);
    setFormData({
      name: '',
      type: 'allowance',
      amount: '',
      percentage: '',
      calculationType: 'fixed',
      calculationBase: 'baseSalary',
      description: '',
      category: '',
      isActive: true,
    });
  };

  const openModal = () => {
    resetForm();
    setFormData(prev => ({ ...prev, type: activeTab === 'allowances' ? 'allowance' : 'deduction' }));
    setShowModal(true);
  };

  const filteredComponents = components.filter(c => {
    if (activeTab === 'allowances') return c.type === 'allowance';
    if (activeTab === 'deductions') return c.type === 'deduction';
    return true;
  });

  const getCategoryColor = (category: string | null) => {
    const colors: Record<string, string> = {
      housing: 'bg-blue-100 text-blue-800',
      transport: 'bg-green-100 text-green-800',
      responsibility: 'bg-purple-100 text-purple-800',
      napsa: 'bg-red-100 text-red-800',
      paye: 'bg-orange-100 text-orange-800',
      levy: 'bg-yellow-100 text-yellow-800',
      absence: 'bg-gray-100 text-gray-800',
    };
    return colors[category || ''] || 'bg-gray-100 text-gray-800';
  };

  if (loading && components.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payroll Components</h1>
        <button
          onClick={openModal}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          + Add Component
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('allowances')}
            className={`pb-2 px-1 border-b-2 font-medium ${
              activeTab === 'allowances'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Allowances ({components.filter(c => c.type === 'allowance').length})
          </button>
          <button
            onClick={() => setActiveTab('deductions')}
            className={`pb-2 px-1 border-b-2 font-medium ${
              activeTab === 'deductions'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Deductions ({components.filter(c => c.type === 'deduction').length})
          </button>
        </nav>
      </div>

      {/* Components List */}
      <div className="bg-white rounded-lg shadow border">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Calculation</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredComponents.map(component => (
                <tr key={component.id} className={`hover:bg-gray-50 ${!component.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{component.name}</p>
                    {component.description && (
                      <p className="text-sm text-gray-500">{component.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${getCategoryColor(component.category)}`}>
                      {component.category || 'General'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      component.type === 'allowance' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {component.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {component.calculationType === 'fixed' 
                      ? 'Fixed Amount' 
                      : component.calculationType === 'percentage'
                      ? `${component.percentage || 0}% of ${component.calculationBase}`
                      : 'Formula'}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {component.calculationType === 'fixed' && component.amount
                      ? formatCurrency(component.amount)
                      : component.calculationType === 'percentage'
                      ? `${component.percentage || 0}%`
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(component)}
                      className={`px-2 py-1 rounded text-xs ${
                        component.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {component.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(component)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(component.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredComponents.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No {activeTab} found. Click "Add Component" to create one.
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingComponent ? 'Edit Component' : 'Add Component'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'allowance' | 'deduction' })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="allowance">Allowance</option>
                  <option value="deduction">Deduction</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Category</option>
                  {formData.type === 'allowance' ? (
                    <>
                      <option value="housing">Housing</option>
                      <option value="transport">Transport</option>
                      <option value="responsibility">Responsibility</option>
                      <option value="overtime">Overtime</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="napsa">NAPSA</option>
                      <option value="paye">PAYE</option>
                      <option value="levy">Personal Levy</option>
                      <option value="loan">Loan</option>
                      <option value="advance">Advance Salary</option>
                      <option value="rent">Rent</option>
                      <option value="schoolfees">School Fees</option>
                      <option value="absence">Absence</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Calculation Type</label>
                <select
                  value={formData.calculationType}
                  onChange={(e) => setFormData({ ...formData, calculationType: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="fixed">Fixed Amount</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>

              {formData.calculationType === 'fixed' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Amount (ZMW)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
              )}

              {formData.calculationType === 'percentage' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Calculation Base</label>
                  <select
                    value={formData.calculationBase}
                    onChange={(e) => setFormData({ ...formData, calculationBase: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="baseSalary">Base Salary</option>
                    <option value="grossSalary">Gross Salary</option>
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollComponents;












